import * as React from "react";
import { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import { useSelector } from "react-redux";
import { Alert, Box, Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import codeTablePostgresApi from "../../apis/codeTablePostgresApi";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import { createChat, deleteChat, listChatMessages, listChatsByUser, saveChatMessage } from "../../apis/aiAssistantChatApi";
import { getSortedMarket } from "../../utilities/helpers";
import ChatComposer from "./chat/ChatComposer";
import ChatEmptyState from "./chat/ChatEmptyState";
import ChatHeader from "./chat/ChatHeader";
import ChatHistoryView from "./chat/ChatHistoryView";
import ChatMessages from "./chat/ChatMessages";
import ChatShell from "./chat/ChatShell";
import ChatSidebar from "./chat/ChatSidebar";
import ConfirmDeleteDialog from "./chat/ConfirmDeleteDialog";
import GapUploadPanel from "./chat/GapUploadPanel";
import { finalizeAnswer, lastResponseIdsByPair, messagesToTurns, pairKey, sortChatsNewestFirst } from "./chat/chatHistory";

const SIDEBAR_COLLAPSED_KEY = "aiChatSidebarCollapsed";

function readSidebarCollapsed() {
    try {
        return sessionStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
        return false;
    }
}

function writeSidebarCollapsed(collapsed) {
    try {
        sessionStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
        // Storage can be unavailable (private mode); the sidebar still works for this view.
    }
}

const uniqueValues = (values) => [...new Set(values)];

export default function AskMeDialogV3({ open, setOpen }) {
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState("");
    const [summaries, setSummaries] = useState([]);
    const [commodities, setCommodities] = useState([]);
    const [selectedCommodities, setSelectedCommodities] = useState([]);
    const [markets, setMarkets] = useState([]);
    const [selectedMarkets, setSelectedMarkets] = useState([]);
    const [vectorStores, setVectorStores] = useState([]);
    const [streamingStatus, setStreamingStatus] = useState({});
    const [isGapAnalysis, setIsGapAnalysis] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    // Last response id per pair: seeded from the saved chat when reopened, then updated as answers arrive.
    const [conversations, setConversations] = useState({});
    const [chats, setChats] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [view, setView] = useState("chat");
    const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
    const [chatToDelete, setChatToDelete] = useState(null);

    const allowedCommodities = useSelector((state) => state.allowedCommodities);
    const allowedMarkets = useSelector((state) => state.allowedMarkets);
    const accessToken = useSelector((state) => state.accessToken);
    const userEmail = useSelector((state) => state.email);

    const abortControllerRef = React.useRef(null);
    // Monotonic counter giving each turn a unique id (spec 008-multiturn-chat FR-005).
    const nextTurnIdRef = React.useRef(0);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [allowedCommodities, allowedMarkets]);

    useEffect(() => {
        if (open) loadChats();
        // eslint-disable-next-line
    }, [open]);

    async function fetchData() {
        const [responseCodeTable] = await Promise.all([codeTablePostgresApi.get(`/codetables/codetabletypes/12,13`)]);
        const codeTables = responseCodeTable.data.body;
        setCommodities(codeTables
            .filter((codeTable) => codeTable.code_table_type_id === 12 && allowedCommodities.includes(codeTable.code))
            .sort((a, b) => a.description.localeCompare(b.description)));
        setMarkets(getSortedMarket(codeTables, allowedMarkets));
        const responseVectorStores = await ieqipCoreServiceApi.get(`/api/commoditymarketvectorstores`);
        setVectorStores(responseVectorStores.data);
    }

    async function loadChats() {
        try {
            const savedChats = await listChatsByUser(userEmail);
            setChats(sortChatsNewestFirst(savedChats));
        } catch (error) {
            console.error("Could not load chat history", error);
            setErrorMessage("Could not load chat history.");
        }
    }

    const closeSidebarOnNarrowScreen = () => {
        if (window.matchMedia("(max-width:767.95px)").matches) toggleSidebar(true);
    };

    const toggleSidebar = (collapsed = !sidebarCollapsed) => {
        setSidebarCollapsed(collapsed);
        writeSidebarCollapsed(collapsed);
    };

    const startNewChat = () => {
        setActiveChat(null);
        setSummaries([]);
        setConversations({});
        setSelectedCommodities([]);
        setSelectedMarkets([]);
        setIsGapAnalysis(false);
        setUploadedFiles([]);
        setDraft("");
        setErrorMessage(null);
        setView("chat");
        closeSidebarOnNarrowScreen();
    };

    const openChat = async (chat) => {
        const savedChat = { uid: chat.ai_assistant_chat_uid, id: chat.id, title: chat.title };
        const scopes = chat.scopes || [];
        setActiveChat(savedChat);
        setSelectedCommodities(uniqueValues(scopes.map((scope) => scope.commodity)));
        setSelectedMarkets(uniqueValues(scopes.map((scope) => scope.market)));
        setView("chat");
        setErrorMessage(null);
        closeSidebarOnNarrowScreen();
        try {
            const messages = await listChatMessages(savedChat.uid);
            setSummaries(messagesToTurns(messages));
            setConversations(lastResponseIdsByPair(messages));
        } catch (error) {
            console.error("Could not open chat", error);
            setErrorMessage("Could not open this chat.");
        }
    };

    const confirmDelete = async () => {
        const chat = chatToDelete;
        setChatToDelete(null);
        try {
            await deleteChat(chat.uid || chat.ai_assistant_chat_uid);
            if (activeChat?.uid === (chat.uid || chat.ai_assistant_chat_uid)) startNewChat();
            await loadChats();
        } catch (error) {
            console.error("Could not delete chat", error);
            setErrorMessage("Could not delete this chat.");
        }
    };

    const handleStreamingResponse = async (turnId, commodity, market, vector_store_id, question, useGapAnalysis = false, files = []) => {
        const summaryKey = pairKey(commodity, market);
        const startTime = Date.now();
        let answerText = "";
        let result = null;

        setStreamingStatus(prev => ({ ...prev, [summaryKey]: { status: 'starting', progress: 0 } }));
        setSummaries(prevSummaries => [
            ...prevSummaries,
            {
                id: turnId,
                commodity,
                market,
                question,
                message: '',
                citations: '',
                isStreaming: true,
                startedAt: startTime,
                endedAt: null,
                elapsed: null,
                errorMessage: null,
                cancelled: false,
            }
        ]);

        try {
            const useGapAnalysisWithFile = useGapAnalysis && files.length > 0;
            const endpoint = useGapAnalysisWithFile
                ? `${process.env.REACT_APP_IEQIP_CORE_SERVICE_API_URL}/api/openai/assistant/proceduregapanalysis`
                : `${process.env.REACT_APP_IEQIP_CORE_SERVICE_API_URL}/api/openai/assistant/askstreaming/v2`;

            let response;

            if (useGapAnalysisWithFile) {
                setStreamingStatus(prev => ({ ...prev, [summaryKey]: { status: 'uploading_file', progress: 20 } }));

                const formData = new FormData();
                formData.append('file', files[0]);
                formData.append('question', question);
                formData.append('vector_store_id', vector_store_id);
                formData.append('commodity', commodity);
                formData.append('market', market);

                setStreamingStatus(prev => ({ ...prev, [summaryKey]: { status: 'analyzing', progress: 40 } }));

                response = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${accessToken}` },
                    body: formData,
                    signal: abortControllerRef.current?.signal,
                });
            } else {
                const askPayload = {
                    question: question,
                    assistant_id: process.env.REACT_APP_AI_ASSISTANT_ID,
                    vector_store_id: vector_store_id,
                    // Continues this pair's thread (spec 008-multiturn-chat). Never set on the Gap Analysis branch (FR-007).
                    previous_response_id: conversations[summaryKey] || null,
                };

                response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(askPayload),
                    signal: abortControllerRef.current?.signal,
                });
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    const endTime = Date.now();
                    const elapsed = (endTime - startTime) / 1000;
                    setSummaries(prevSummaries =>
                        prevSummaries.map(summary =>
                            summary.id === turnId ? { ...summary, isStreaming: false, endedAt: endTime, elapsed } : summary
                        )
                    );
                    setStreamingStatus(prev => ({ ...prev, [summaryKey]: { status: 'completed' } }));
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const data = JSON.parse(line.slice(6));

                        if (data.type === 'delta' && data.content) {
                            answerText += data.content;
                            flushSync(() => {
                                setSummaries(prevSummaries =>
                                    prevSummaries.map(summary =>
                                        summary.id === turnId ? { ...summary, message: summary.message + data.content } : summary
                                    )
                                );
                                setStreamingStatus(prev => ({ ...prev, [summaryKey]: { status: 'streaming' } }));
                            });
                        } else if (data.type === 'rewrite' && typeof data.content === 'string') {
                            // Backend rewrote the message so inline [N] markers align with the References list.
                            answerText = data.content;
                            setSummaries(prevSummaries =>
                                prevSummaries.map(summary => (summary.id === turnId ? { ...summary, message: data.content } : summary))
                            );
                        } else if (data.type === 'done') {
                            const citationsList = Array.isArray(data.citations) ? data.citations : [];
                            const citations = citationsList.join('\n');
                            const citedCount = Number.isInteger(data.cited_count) ? data.cited_count : citationsList.length;
                            const message = finalizeAnswer(answerText, data.annotation_map || {});
                            if (data.response_id) {
                                setConversations(prev => ({ ...prev, [summaryKey]: data.response_id }));
                            }
                            setSummaries(prevSummaries =>
                                prevSummaries.map(summary =>
                                    summary.id === turnId ? { ...summary, isStreaming: false, citations, citedCount, message } : summary
                                )
                            );
                            setStreamingStatus(prev => ({ ...prev, [summaryKey]: { status: 'completed' } }));
                            result = { message, citations, responseId: data.response_id ?? null };
                        } else if (data.type === 'error') {
                            throw new Error(data.message || 'An error occurred');
                        }
                    } catch (e) {
                        console.error('Error parsing SSE data:', e, line);
                    }
                }
            }
            return result;
        } catch (error) {
            const endTime = Date.now();
            const aborted = error?.name === 'AbortError';
            const errMsg = error?.message || '';
            const isTimeoutLike = !aborted && /failed to fetch|load failed|networkerror|status:\s*(504|502|503|408)/i.test(errMsg);

            const friendlyError = aborted
                ? null
                : isTimeoutLike
                    ? `${commodity} / ${market} took too long to respond — the document store may be large or under heavy load. Please try again in a moment.`
                    : `We couldn't get a response for ${commodity} / ${market}. Please try again.`;

            if (aborted) {
                setStreamingStatus(prev => ({ ...prev, [summaryKey]: { status: 'cancelled' } }));
            } else {
                console.error(`Error streaming ${commodity}/${market}:`, error);
                setStreamingStatus(prev => ({ ...prev, [summaryKey]: { status: 'error', progress: 0 } }));
            }

            setSummaries(prevSummaries =>
                prevSummaries.map(summary =>
                    summary.id === turnId
                        ? { ...summary, isStreaming: false, cancelled: aborted, errorMessage: friendlyError, endedAt: endTime, elapsed: (endTime - startTime) / 1000 }
                        : summary
                )
            );

            // Stop processing any remaining pairs.
            if (aborted) throw error;
            return null;
        }
    };

    const resolvePairs = () => {
        const pairs = [];
        for (const commodity of selectedCommodities) {
            for (const market of selectedMarkets) {
                const commodity_id = commodities.filter(commodityItem => commodityItem.code === commodity)[0].id;
                const market_id = markets.filter(marketItem => marketItem.code === market)[0].id;
                const vector_store_id =
                    vectorStores.filter(vectorStore => vectorStore.commodity_id === commodity_id && vectorStore.market_id === market_id)[0]?.vector_store_id;

                if (!vector_store_id) {
                    throw Error(`Vector store not found for ${commodity}/${market}. Please upload first document to ${commodity}/${market}`);
                }

                pairs.push({ commodity, market, vector_store_id });
            }
        }
        return pairs;
    };

    const onHandleSend = async (e) => {
        e.preventDefault();
        if (loading) return;

        try {
            if (!draft.trim()) return;

            if (!hasScope) {
                setErrorMessage("Select at least one Commodity and one Market before asking a question.");
                return;
            }

            if (isGapAnalysis && uploadedFiles.length === 0) {
                setErrorMessage("Please upload a file to perform Gap and Analysis");
                return;
            }

            const question = draft;
            setDraft("");
            setErrorMessage(null);
            setLoading(true);
            abortControllerRef.current = new AbortController();

            // Resolve every pair's store before sending anything, so a missing store cannot leave earlier pairs answered.
            const pairs = resolvePairs();

            // Gap Analysis file runs are not saved to history (spec FR-022).
            const persist = !(isGapAnalysis && uploadedFiles.length > 0);
            let chat = activeChat;
            if (persist) {
                if (!chat) {
                    const created = await createChat({
                        userId: userEmail,
                        scopes: pairs.map(({ commodity, market }) => ({ commodity, market })),
                    });
                    chat = { uid: created.ai_assistant_chat_uid, id: created.id, title: null };
                    setActiveChat(chat);
                }
                await saveChatMessage({ chatId: chat.id, userId: userEmail, userName: userEmail, message: question });
            }

            for (const { commodity, market, vector_store_id } of pairs) {
                const turnId = ++nextTurnIdRef.current;
                const answer = await handleStreamingResponse(turnId, commodity, market, vector_store_id, question, isGapAnalysis, uploadedFiles);
                if (persist && answer) {
                    await saveChatMessage({
                        chatId: chat.id,
                        userId: userEmail,
                        userName: userEmail,
                        isAssistant: true,
                        commodity,
                        market,
                        message: answer.message,
                        citations: answer.citations,
                        openaiResponseId: answer.responseId,
                    });
                }
            }

            if (persist) await loadChats();
        } catch (error) {
            if (error?.name === 'AbortError') {
                setErrorMessage("Chat cancelled.");
            } else {
                console.error("General error:", error);
                setErrorMessage(`General error: ${error}`);
            }
        } finally {
            setLoading(false);
            abortControllerRef.current = null;
        }
    };

    const handleCancel = () => {
        abortControllerRef.current?.abort();
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleGapToggle = (checked) => {
        setIsGapAnalysis(checked);
        if (!checked) setUploadedFiles([]);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const maxSizeInBytes = 1 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            setErrorMessage(`File size exceeds 1MB limit. Selected file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
            event.target.value = null;
            return;
        }

        setUploadedFiles([file]);
        setErrorMessage(null);
        event.target.value = null;
    };

    const handleRemoveFile = () => {
        setUploadedFiles([]);
    };

    const handleDonwload = async (commodity, market, original_filename) => {
        try {
            setErrorMessage(null);
            let response = await ieqipCoreServiceApi.get(`/api/aiassistantdocuments/commodity/${commodity}/market/${market}/filename/${encodeURIComponent(original_filename)}`);
            const uploaded_filename = response.data.uploaded_filename;
            const resolved_filename = response.data.original_filename || original_filename;

            const bucketName = process.env.REACT_APP_S3_IEQIP_AI_DOCUMENT;
            const folder = `${commodity}_${market}_${process.env.REACT_APP_ENVIRONMENT}`;
            response = await ieqipCoreServiceApi.get(`/api/s3/v2/download/${bucketName}/${folder}/${encodeURIComponent(uploaded_filename)}`, {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", resolved_filename || uploaded_filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
            const status = error?.response?.status;
            setErrorMessage(
                status === 404
                    ? `Could not find "${original_filename}" to download. It may have been removed from the document store.`
                    : `Failed to download "${original_filename}". Please try again.`
            );
        }
    };

    const selectionLocked = Boolean(activeChat);
    const hasScope = selectedCommodities.length > 0 && selectedMarkets.length > 0;
    const canSend = hasScope && Boolean(draft.trim()) && !(isGapAnalysis && uploadedFiles.length === 0);
    const isHistoryView = view === "history";
    const isNewChat = !isHistoryView && !activeChat && summaries.length === 0;

    const header = (
        <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {activeChat && (
                    <ChatHeader title={activeChat.title} onRequestDelete={() => setChatToDelete(activeChat)} />
                )}
            </Box>
            <IconButton aria-label="Close" onClick={handleClose}>
                <CloseIcon />
            </IconButton>
        </Box>
    );

    const body = isHistoryView
        ? <ChatHistoryView chats={chats} activeChatUid={activeChat?.uid} onSelectChat={openChat} onRequestDelete={setChatToDelete} />
        : <ChatMessages turns={summaries} streamingStatus={streamingStatus} onDownload={handleDonwload} />;

    const footer = isHistoryView ? null : (
        <>
            {errorMessage && <Alert severity="error" sx={{ mb: 1.5, mx: "auto", maxWidth: 760 }}>{errorMessage}</Alert>}
            {isGapAnalysis && (
                <GapUploadPanel files={uploadedFiles} onFileChange={handleFileUpload} onRemove={handleRemoveFile} />
            )}
            <ChatComposer
                value={draft}
                onChange={setDraft}
                onSend={onHandleSend}
                onCancel={handleCancel}
                loading={loading}
                canSend={canSend}
                commodities={commodities}
                markets={markets}
                selectedCommodities={selectedCommodities}
                selectedMarkets={selectedMarkets}
                onCommodityChange={setSelectedCommodities}
                onMarketChange={setSelectedMarkets}
                selectionLocked={selectionLocked}
                scopeMissing={!hasScope}
                isGapAnalysis={isGapAnalysis}
                onGapToggle={handleGapToggle}
            />
        </>
    );

    return (
        <div>
            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: { xs: "100vw", md: 1040 },
                        maxWidth: "100vw",
                        overflow: "hidden",
                    },
                }}
            >
                <ChatShell
                    sidebar={
                        <ChatSidebar
                            chats={chats}
                            activeChatUid={activeChat?.uid}
                            view={view}
                            onNewChat={startNewChat}
                            onShowChats={() => setView("history")}
                            onSelectChat={openChat}
                            onRequestDelete={setChatToDelete}
                            onToggleCollapse={() => toggleSidebar(true)}
                        />
                    }
                    header={header}
                    empty={<ChatEmptyState />}
                    body={body}
                    footer={footer}
                    isNewChat={isNewChat}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={() => toggleSidebar()}
                />
            </Drawer>
            <ConfirmDeleteDialog chat={chatToDelete} onCancel={() => setChatToDelete(null)} onConfirm={confirmDelete} />
        </div>
    );
}
